import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Button } from "@/components/ui/button";
import dayjs from "dayjs";

import React from "react";

import "./style.css";
import { UserWithCompanies } from "@/components/auth";
import { useAppStore, selectUser, AppStoreState } from "@/store/appStore";
import { useEffect } from "react";

import { QUARTERS } from "@/constants";
import { getQuarterFromMonth } from "@/utils/get-quarter-from-month";
import { ChevronDown } from "lucide-react";

export const saveSelectedDate = (data: {
  year: number;
  month: number | null;
}): Promise<Response> => {
  return fetch("/api/user/setSelectedDate", {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify({
      selectedYear: data.year,
      selectedQuarter:
        data.month === null ? undefined : getQuarterFromMonth(data.month),
      selectedMonth: data.month,
    }),
  });
};

export function DatePicker(): React.ReactElement {
  // Track last intended selection to prevent backend flicker
  const pendingSelectionRef = React.useRef<number | string | null>(null);
  // Track if 'All' was the last selection
  const lastSelectionRef = React.useRef<string | number>(null);
  // Loading state for backend response
  const [isLoading, setIsLoading] = React.useState(false);
  const user = useAppStore(selectUser);
  const setUser = useAppStore((s) => s.setUser);
  const years = [
    "All",
    ...Array.from({ length: 51 }, (_, i) => new Date().getFullYear() - i),
  ];
  const currentYear = new Date().getFullYear();
  // Local state for instant UI feedback
  // Always initialize to 'All' if backend user state has all date fields as null
  const isAll =
    user?.selectedYear == null &&
    user?.selectedMonth == null &&
    user?.selectedQuarter == null;
  const [localYear, setLocalYear] = React.useState<number | string>(() => {
    if (isAll) return "All";
    if (user?.selectedYear) return user.selectedYear;
    return currentYear;
  });
  const [localMonth, setLocalMonth] = React.useState<number | null>(() => {
    if (isAll) return null;
    if (user?.selectedMonth) return user.selectedMonth;
    return new Date().getMonth() + 1;
  });

  // Sync local state with store when user changes (backend response)
  React.useEffect(() => {
    // Only update local state from backend if it matches the last intended selection
    const isAll =
      user?.selectedYear == null &&
      user?.selectedMonth == null &&
      user?.selectedQuarter == null;
    if (lastSelectionRef.current === "All") {
      if (isAll) {
        if (localYear !== "All" || localMonth !== null) {
          setLocalYear("All");
          setLocalMonth(null);
        }
      }
    } else if (typeof lastSelectionRef.current === "number") {
      if (user?.selectedYear === lastSelectionRef.current) {
        if (user.selectedYear !== localYear) {
          setLocalYear(user.selectedYear);
        }
        if (user.selectedMonth !== localMonth) {
          setLocalMonth(user.selectedMonth);
        }
      }
      // If backend returns null, ignore and keep localYear as the intended selection
    }
    // Always update lastSelectionRef to reflect latest intent
    lastSelectionRef.current = isAll ? "All" : user?.selectedYear;
  }, [
    user?.selectedYear,
    user?.selectedMonth,
    user?.selectedQuarter,
    localMonth,
    localYear,
  ]);

  // UI values
  // If 'All' is selected, no month should be selected/highlighted
  let selectedMonth: string = "";
  const selectedMonthNumeric: number | null =
    localYear === "All" ? null : localMonth ?? new Date().getMonth() + 1;
  if (
    typeof selectedMonthNumeric === "number" &&
    typeof localYear === "number"
  ) {
    const month = new Date();
    month.setFullYear(localYear);
    month.setMonth(selectedMonthNumeric - 1);
    selectedMonth = dayjs(month).format("MMM");
  }
  const selectedQuarter = getQuarterFromMonth(selectedMonthNumeric ?? 1);
  const showAll = useAppStore((s: AppStoreState) => s.showAllData);
  const mutateSelectedDate = async (data: {
    year: number | string;
    month: number | null;
  }) => {
    setIsLoading(true);
    let newUser;
    if (data.year === "All") {
      // Send nulls to backend for 'All'
      await fetch("/api/user/setSelectedDate", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          selectedYear: null,
          selectedQuarter: null,
          selectedMonth: null,
        }),
      });
      const userRes = await fetch("/api/user");
      newUser = await userRes.json();
      console.log("[DatePicker] Received user after 'All' selection:", newUser);
      setUser(newUser);
      setLocalYear("All");
      setLocalMonth(null);
      lastSelectionRef.current = "All";
      setIsLoading(false);
      return;
    }
    // If switching away from 'All', reset local state and send to backend
    setLocalYear(data.year);
    setLocalMonth(data.month);
    lastSelectionRef.current = data.year;
    setUser((prev: UserWithCompanies | null) => {
      if (!prev) return prev;
      return {
        ...prev,
        selectedYear: typeof data.year === "string" ? null : data.year,
        selectedMonth: data.month,
        selectedQuarter:
          data.month === null ? null : getQuarterFromMonth(data.month),
      };
    });
    await saveSelectedDate({
      year: typeof data.year === "string" ? parseInt(data.year) : data.year,
      month: data.month,
    });
    const userRes = await fetch("/api/user");
    newUser = await userRes.json();
    console.log(
      "[DatePicker] Received user after year/month selection:",
      newUser
    );
    // Only update local state from backend if it differs from what was just set
    if (newUser?.selectedYear == null && newUser?.selectedMonth == null) {
      if (localYear !== "All" || localMonth !== null) {
        setLocalYear("All");
        setLocalMonth(null);
      }
    } else {
      if (newUser.selectedYear !== localYear)
        setLocalYear(newUser.selectedYear);
      if (newUser.selectedMonth !== localMonth)
        setLocalMonth(newUser.selectedMonth);
    }
    setUser(newUser);
    setIsLoading(false);
  };

  const onSetMonth = (month: { name: string; value: number }) => {
    // Update local state immediately for instant feedback
    setLocalMonth(month.value ?? 1);
    mutateSelectedDate({ year: localYear, month: month.value ?? 1 });
  };
  const onSetYear = (year: number | string) => {
    pendingSelectionRef.current = year;
    // Update local state immediately for instant feedback
    if (year === "All") {
      setLocalYear("All");
      setLocalMonth(null);
      // Call backend after local state update
      mutateSelectedDate({ year: "All", month: null });
    } else {
      const yearNum = typeof year === "string" ? parseInt(year) : year;
      setLocalYear(yearNum);
      setLocalMonth(null);
      // Call backend after local state update
      mutateSelectedDate({ year: yearNum, month: null });
    }
  };

  return (
    <DropdownMenu modal={true}>
      <Button
        asChild
        variant={"outline"}
        className={
          "account-menu-trigger !p-xs space-x-0 h-fit border flex flex-row hover:bg-foreground/10"
        }
      >
        <DropdownMenuTrigger>
          <div className="flex flex-col min-w-[60px] shadow-sm relative">
            <div
              className={
                localYear === "All" ||
                (typeof localYear === "number" && localMonth === null)
                  ? "bg-error-400 p-xs w-full rounded-sm text-foreground-light prose-caption-12-bold flex items-center justify-center"
                  : "bg-error-400 p-xs w-full rounded-t-sm text-foreground-light prose-caption-12-bold h-full"
              }
              style={
                localYear === "All" ||
                (typeof localYear === "number" && localMonth === null)
                  ? { height: 44 }
                  : {}
              }
            >
              {localYear}
            </div>
            {localYear !== "All" && localMonth !== null && (
              <div
                className={
                  "bg-background-context p-xs rounded-b-sm prose-caption-12-semibold uppercase "
                }
              >
                {selectedMonth}
              </div>
            )}
          </div>
          <ChevronDown className={"ml-2"} size={16} />
        </DropdownMenuTrigger>
      </Button>
      <DropdownMenuContent align="start">
        <div className="year-picker-container">
          {years.map((year, index) => (
            <DropdownMenuItem
              className={
                year === localYear
                  ? "selected-year year-menu-item"
                  : "year-menu-item"
              }
              key={index}
              onClick={() => onSetYear(year === "All" ? "All" : Number(year))}
            >
              {year === "All" ? "All" : year}
            </DropdownMenuItem>
          ))}
        </div>
        <div className="month-picker-container">
          {QUARTERS &&
            Object.keys(QUARTERS).map((quarter, index: number) => {
              const typedQuarter = quarter as keyof typeof QUARTERS;
              return (
                <div
                  className={
                    quarter == "Q" + selectedQuarter
                      ? "quarter-option selected-quarter-option"
                      : "quarter-option"
                  }
                  key={`quarter-option-${index}`}
                >
                  <div
                    className={
                      quarter == "Q" + selectedQuarter
                        ? "quarter-option-title selected-quarter"
                        : "quarter-option-title"
                    }
                  >
                    {quarter}
                  </div>
                  <div className="months-options">
                    {QUARTERS[typedQuarter].map((month, idx) => (
                      <DropdownMenuItem
                        className={
                          localYear === "All"
                            ? "disabled-month"
                            : localMonth !== null && month.value === localMonth
                            ? "selected-month"
                            : ""
                        }
                        key={idx}
                        onClick={
                          localYear === "All"
                            ? undefined
                            : () => onSetMonth(month)
                        }
                        disabled={localYear === "All"}
                      >
                        {month.name}
                      </DropdownMenuItem>
                    ))}
                  </div>
                </div>
              );
            })}
        </div>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
